import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const BOT = `${API}/bot`;

export const api = axios.create({ baseURL: API, timeout: 15000, withCredentials: true });

api.interceptors.response.use(
    (r) => r,
    (err) => {
        if (err?.response?.status === 401) {
            window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        }
        return Promise.reject(err);
    },
);

export const fetchStatus = () => api.get("/bot/status").then(r => r.data);
export const fetchGrid = () => api.get("/bot/grid").then(r => r.data);
export const fetchOrders = (status) => api.get("/bot/orders", { params: status ? { status } : {} }).then(r => r.data);
export const fetchTrades = (limit = 200) => api.get("/bot/trades", { params: { limit } }).then(r => r.data);
export const fetchPnl = () => api.get("/bot/pnl").then(r => r.data);
export const fetchPnlDaily = (days = 30) => api.get("/bot/pnl/daily", { params: { days } }).then(r => r.data);
export const fetchTicker = () => api.get("/bot/ticker").then(r => r.data);
export const fetchPriceHistory = () => api.get("/bot/price-history").then(r => r.data);
export const fetchEvents = (limit = 200) => api.get("/bot/events", { params: { limit } }).then(r => r.data);
export const fetchConfig = () => api.get("/bot/config").then(r => r.data);
export const fetchTelemetry = () => api.get("/bot/telemetry").then(r => r.data);
export const fetchOrderEvents = (params = {}) => api.get("/bot/order-events", { params }).then(r => r.data);

export const startBot = (mode) => api.post("/bot/start", { mode }).then(r => r.data);
export const stopBot = () => api.post("/bot/stop").then(r => r.data);
export const setMode = (mode) => api.post("/bot/mode", { mode }).then(r => r.data);
export const setNetwork = (useTestnet) => api.post("/bot/network", { use_testnet: useTestnet }).then(r => r.data);
export const setKillSwitch = (on) => api.post("/bot/kill-switch", { on }).then(r => r.data);
export const updateConfig = (cfg) => api.put("/bot/config", cfg).then(r => r.data);
export const recover = () => api.post("/bot/recover").then(r => r.data);

// Server load / system metrics (Ubuntu VPS)
export const fetchSystemLoad = () => api.get("/system/load").then(r => r.data);

// Multi-grid manager (concurrent grids, one per pair)
export const fetchGrids = () => api.get("/grids").then(r => r.data);
export const createGrid = (payload) => api.post("/grids", payload).then(r => r.data);
export const deleteGrid = (gid) => api.delete(`/grids/${gid}`).then(r => r.data);
export const startGrid = (gid, mode) => api.post(`/grids/${gid}/start`, { mode }).then(r => r.data);
export const stopGrid = (gid) => api.post(`/grids/${gid}/stop`).then(r => r.data);
export const setGridMode = (gid, mode) => api.post(`/grids/${gid}/mode`, { mode }).then(r => r.data);
export const setGridKill = (gid, on) => api.post(`/grids/${gid}/kill-switch`, { on }).then(r => r.data);
export const updateGridConfig = (gid, cfg) => api.post(`/grids/${gid}/config`, cfg).then(r => r.data);
export const fetchGridConfig = (gid) => api.get(`/grids/${gid}/config`).then(r => r.data);
export const setGridCredentials = (gid, signer_address, private_key) => api.post(`/grids/${gid}/credentials`, { signer_address, private_key }).then(r => r.data);
export const clearGridCredentials = (gid) => api.delete(`/grids/${gid}/credentials`).then(r => r.data);
export const fetchGridStatus = (gid) => api.get(`/grids/${gid}/status`).then(r => r.data);
export const fetchGridSnapshot = (gid) => api.get(`/grids/${gid}/grid`).then(r => r.data);
export const fetchGridOrders = (gid, status = "open") => api.get(`/grids/${gid}/orders`, { params: { status } }).then(r => r.data);
export const fetchGridTrades = (gid, limit = 200) => api.get(`/grids/${gid}/trades`, { params: { limit } }).then(r => r.data);
export const fetchGridPriceHistory = (gid) => api.get(`/grids/${gid}/price-history`).then(r => r.data);

// Tradable pairs list (used by the multi-grid create form)
export const fetchSymbols = () => api.get("/bot/symbols").then(r => r.data);
export const resetPaper = (btc = 0.5, usdt = 50000) => api.post(`/bot/paper/reset?btc=${btc}&usdt=${usdt}`).then(r => r.data);

// Phase 4: exclusion ranges
export const fetchExclusions = () => api.get("/bot/exclusions").then(r => r.data);
export const addExclusion = (min, max, label) => api.post("/bot/exclusions", { min, max, label }).then(r => r.data);
export const removeExclusion = (id) => api.delete(`/bot/exclusions/${id}`).then(r => r.data);

// Phase 4: per-grid drill-down
export const fetchGridDetail = (gridId) => api.get(`/bot/grid/${gridId}`).then(r => r.data);

export const EVENTS_STREAM_URL = `${BOT}/events/stream`;
export const exportTradesCsvUrl = () => `${BOT}/export/trades.csv`;
export const exportPnlDailyCsvUrl = (days = 90) => `${BOT}/export/pnl-daily.csv?days=${days}`;

// Trade history page
export const fetchHistoryTrades = (params = {}) => api.get("/bot/history/trades", { params }).then(r => r.data);
export const fetchHistoryStats = (params = {}) => api.get("/bot/history/stats", { params }).then(r => r.data);
export const fetchHistoryIntegrity = (params = {}) => api.get("/bot/history/integrity", { params }).then(r => r.data);
export const exportHistoryCsvUrl = (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")).toString();
    return `${BOT}/history/export.csv${qs ? `?${qs}` : ""}`;
};

// Group sell + orphans
export const fetchGroupSellActive = () => api.get("/bot/group-sell/active").then(r => r.data);
export const previewGroupSell = (source, gridIds) => api.post("/bot/group-sell/preview", { source, grid_ids: gridIds }).then(r => r.data);
export const executeGroupSell = (payload) => api.post("/bot/group-sell/execute", payload).then(r => r.data);
export const fetchOrphans = () => api.get("/bot/orphans").then(r => r.data);
export const resolveOrphansManual = (gridIds, sellPrice) => api.post("/bot/orphans/resolve-manual", { grid_ids: gridIds, sell_price: sellPrice }).then(r => r.data);
export const fetchCombos = () => api.get("/bot/combos").then(r => r.data);
export const cancelCombo = (comboId) => api.post(`/bot/combos/${comboId}/cancel`).then(r => r.data);
